import { useState, useRef, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import AlarmsContent from './AlarmsContent';
import HistoryLogContent from './HistoryLogContent';
import { InstalledProductResponse } from './shared';
import { PermissionsList } from '@/constants/permissionsList';
import { AlarmData } from '@/slices/types';
import { RootState } from '@/store';

interface AlarmsTabProps {
  installedProductData?: InstalledProductResponse;
  historyData?: AlarmData[];
  isLoadingHistory?: boolean;
  historyError?: Error | null;
  installedProductError?: Error | null;
  onHistoryFilterChange?: (startDate: string, endDate: string) => void;
  turbineId?: string;
  activeAlarmsTabId: string;
}

export default function AlarmsTab({
  installedProductData,
  historyData,
  isLoadingHistory = false,
  historyError = null,
  installedProductError = null,
  onHistoryFilterChange,
  turbineId,
  activeAlarmsTabId
}: AlarmsTabProps) {
  const auditableElements = useSelector(
    (state: RootState) => state.auditableElements?.authorizations?.permissions
  ) as PermissionsList;

  const turbinePermissions = auditableElements?.TURBINE_DETAILS || {};
  const showHistoryLog = turbinePermissions?.HISTORY_LOG;

  const tabIdRef = useRef<string>('alarms');
  const [activeTabId, setActiveTabId] = useState<string>(
    () => tabIdRef.current
  );

  useEffect(() => {
    tabIdRef.current = activeAlarmsTabId;
    setActiveTabId(activeAlarmsTabId);
  }, [activeAlarmsTabId]);

  useEffect(() => {
    if (tabIdRef.current !== activeTabId) {
      setActiveTabId(tabIdRef.current);
    }
  }, [historyData, isLoadingHistory]);

  const historyLogs = useMemo(() => {
    if (!historyData) return [];
    return Array.isArray(historyData) ? historyData : [];
  }, [historyData]);

  return (
    <>
      {activeTabId === 'alarms' && (
        <AlarmsContent
          installedProductData={installedProductData}
          installedProductError={installedProductError}
          turbineId={turbineId}
        />
      )}
      {activeTabId === 'historyLog' && (
        <HistoryLogContent
          historyData={historyLogs}
          isLoading={isLoadingHistory}
          error={historyError as Error | null}
          onFilterChange={onHistoryFilterChange}
        />
      )}
    </>
  );
}
